import {Text} from '@mantine/core';
import React, {ReactNode, useEffect} from 'react';

import {assert} from 'Asserts';
import {Permissions} from 'auth/PermissionConstants';
import {MODEL_RUN_SOURCE} from 'constants/AnalyticsConstants';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import ModelHubBulkBanner from 'model-hub/ModelHubBulkBanner';
import {
  type ScientificModelNode,
  type ScientificModelsByExternalReference,
} from 'model-hub/scientific-models/ScientificModelMetadata';
import {
  InProgressPredictionData,
  CoordinateStructuresList,
} from 'model-hub/scientific-models/structure-prediction/CoordinateStructureList';
import {
  type AaSequenceInput,
  type ProteinInput,
  getRunDisabledMessage,
  AvailableStructureInfo,
  getCoordinateStructureId,
} from 'model-hub/scientific-models/structure-prediction/StructurePredictionUtils';
import {
  useStructurePredictionViewState,
  ViewState,
} from 'model-hub/scientific-models/structure-prediction/structurePredictionViewState';
import {CenteredSpinner} from 'ui-library/Spinner';

import StructurePredictionForm from '../input-forms/structure-prediction/StructurePredictionForm';
import {useRunAllModels} from './useRunAllModels';

export interface StructurePredictionTabProps {
  availableModels: ReadonlyArray<ScientificModelNode>;
  structurePredictionQueryStatus: QueryResultType;
  inProgressStructurePredictions: Array<InProgressPredictionData>;
  completedStructurePredictions: Array<AvailableStructureInfo>;
  initialView?: ViewState;
  onViewStateChange?: (viewState: ViewState) => void;
  permissions: Permissions;
  catalogByExternalReference: ScientificModelsByExternalReference;
  onSelectCoordinateStructureId: (coordinateStructureId: string) => void;
  onSubmitNewRun: () => void;
  children: (controls: {setViewState: (viewState: ViewState) => void}) => ReactNode;
  inputComponent: ProteinInput | AaSequenceInput;
  versionId: string | number;
}

export default function StructurePredictionTab({
  availableModels,
  structurePredictionQueryStatus,
  inProgressStructurePredictions,
  completedStructurePredictions,
  initialView,
  onViewStateChange,
  permissions,
  catalogByExternalReference,
  onSelectCoordinateStructureId,
  onSubmitNewRun,
  children,
  inputComponent,
  versionId,
}: StructurePredictionTabProps): JSX.Element {
  const hasCompletedStructures = completedStructurePredictions.length > 0;
  const hasInProgressPredictions = inProgressStructurePredictions.length > 0;
  // Only the protein-entity flow carries a string version id (`cpstvrsn_...`); the aa-sequence flow
  // passes a numeric db id, which is not the key for `proteinVersionPredictionJobs` eviction.
  const primaryComponentVersionId = typeof versionId === 'string' ? versionId : undefined;

  const {viewState, setViewState} = useStructurePredictionViewState({
    structurePredictionQueryStatus,
    hasCompletedStructures,
    hasInProgressPredictions,
    initialView,
  });

  useEffect(() => {
    onViewStateChange?.(viewState);
  }, [viewState, onViewStateChange]);

  const isListView = viewState === ViewState.COORDINATE_STRUCTURES_LIST;

  const {handleRunAllModels, submitting: runAllModelsSubmitting} = useRunAllModels({
    models: availableModels,
    inputComponent,
    // From the form view, StructurePredictionForm's onSubmit already refreshes after a queued run-all,
    // so only refresh from the hook in the list view to avoid a duplicate refetch.
    onSubmitRunAllModels: isListView ? onSubmitNewRun : undefined,
    isListView,
    versionId,
  });

  switch (viewState) {
    case ViewState.LOADING:
      return <CenteredSpinner />;

    case ViewState.ERROR:
      return (
        <Text p="lg" size="sm">
          Error when loading structure prediction details. Please refresh the page.
        </Text>
      );

    case ViewState.COORDINATE_STRUCTURES_LIST: {
      const disabledMessage = getRunDisabledMessage({
        hasAvailableModels: availableModels.length > 0,
        structurePredictionEditable: permissions.structurePredictionEditable === true,
      });

      return (
        <>
          <ModelHubBulkBanner />
          <CoordinateStructuresList
            coordinateStructures={completedStructurePredictions}
            onSelectCoordinateStructure={(coordinateStructure) => {
              const coordinateStructureId = getCoordinateStructureId(coordinateStructure);
              onSelectCoordinateStructureId?.(coordinateStructureId);
              setViewState(ViewState.VIEWER);
            }}
            onCreateRun={() => {
              setViewState(ViewState.RUN_STRUCTURE_PREDICTION);
            }}
            onRunAllModels={handleRunAllModels}
            inProgressStructurePredictions={inProgressStructurePredictions}
            disabledMessage={disabledMessage}
            runAllModelsDisabled={runAllModelsSubmitting}
            catalogByExternalReference={catalogByExternalReference}
          />
        </>
      );
    }

    case ViewState.VIEWER: {
      return <>{children({setViewState})}</>;
    }

    case ViewState.RUN_STRUCTURE_PREDICTION:
      return (
        <>
          <ModelHubBulkBanner />
          <StructurePredictionForm
            availableModels={availableModels}
            inputComponent={inputComponent}
            primaryComponentVersionId={primaryComponentVersionId}
            onSubmit={() => {
              onSubmitNewRun();
              setViewState(ViewState.COORDINATE_STRUCTURES_LIST);
            }}
            onBack={() => {
              setViewState(ViewState.COORDINATE_STRUCTURES_LIST);
            }}
            onRunAllModels={handleRunAllModels}
            source={MODEL_RUN_SOURCE.STRUCTURE_TAB}
          />
        </>
      );
    default:
      assert(false, 'Invalid view state');
  }
}
